import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchSalesOverview } from '../../api/analytics';
import { syncOrders } from '../../api/orders';
import { waitForSyncJob } from '../../api/syncJobs';
import { invalidateCache, useCachedResource } from '../../hooks/useCachedResource';
import { notifyShippingAlertsRefresh } from '../orders/shippingAlertsRefresh';
import { panelClassName } from '../../components/ui';

function formatMoney(value, currency = 'INR') {
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(value || 0);
  } catch {
    return `${currency} ${Number(value || 0).toLocaleString()}`;
  }
}

/** Format a Date as d/m/yyyy in Asia/Kolkata (matches Seller Central India). */
function formatIstDateString(date = new Date()) {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).format(date);
}

function getIstCalendarDate(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  return `${year}-${month}-${day}`;
}

function getIstYesterdayDate() {
  const todayStr = getIstCalendarDate();
  const todayStart = new Date(`${todayStr}T00:00:00+05:30`);
  return new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
}

function formatTimestamp(isoString) {
  if (!isoString) return '—';
  const date = new Date(isoString);
  return (
    date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }) + ' IST'
  );
}

function formatHourLabel(h) {
  if (h === 0) return '12 a.m.';
  if (h === 12) return '12 p.m.';
  return h < 12 ? `${h} a.m.` : `${h - 12} p.m.`;
}

const SERIES_CONFIG = {
  today: {
    label: 'Today so far',
    color: 'rgb(var(--crm-accent))',
    swatch: 'bg-[rgb(var(--crm-accent))]',
  },
  yesterday: {
    label: 'Yesterday',
    color: '#dc2626',
    swatch: 'bg-red-600',
  },
  lastMonth: {
    label: 'Same day last month',
    color: '#ea580c',
    swatch: 'bg-orange-600',
  },
  lastYear: {
    label: 'Same day last year',
    color: '#64748b',
    swatch: 'bg-slate-500',
  },
};

const selectClassName =
  'rounded-lg border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-input-bg))] px-3 py-1.5 text-xs font-medium text-[rgb(var(--crm-text))] shadow-xs transition hover:border-[rgb(var(--crm-accent)/0.5)] focus:border-[rgb(var(--crm-accent))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--crm-accent)/0.15)] cursor-pointer';

function ComparisonChart({ title, seriesData, valueKey, formatValue, activeSeries, loading }) {
  const svgRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const width = 500;
  const height = 230;
  const left = 50;
  const right = 16;
  const top = 16;
  const bottom = 30;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;

  const activeKeys = Object.keys(SERIES_CONFIG).filter((key) => activeSeries[key]);
  const maxVal = useMemo(() => {
    let max = 1;
    for (const key of activeKeys) {
      const data = seriesData[key] || [];
      const seriesMax = Math.max(...data.map((p) => p[valueKey] || 0), 0);
      if (seriesMax > max) max = seriesMax;
    }
    return max;
  }, [seriesData, valueKey, activeKeys]);

  const step = plotWidth / 23;
  const gridLines = [0, 0.25, 0.5, 0.75, 1];
  const gradPrefix = title.toLowerCase().replace(/[^a-z0-9]/g, '-');

  const handleMouseMove = (event) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    const x = ((clientX - rect.left) / rect.width) * width;

    if (x < left || x > width - right) {
      setHoverIndex(null);
      return;
    }
    const hour = Math.round((x - left) / step);
    setHoverIndex(Math.max(0, Math.min(23, hour)));
  };

  const activePointsAtHover = useMemo(() => {
    if (hoverIndex === null) return [];
    return activeKeys
      .map((key) => {
        const data = seriesData[key] || [];
        const point = data.find((p) => p.hour === hoverIndex);
        return {
          key,
          label: SERIES_CONFIG[key].label,
          color: SERIES_CONFIG[key].color,
          value: point ? point[valueKey] || 0 : null,
        };
      })
      .filter((p) => p.value !== null);
  }, [hoverIndex, seriesData, valueKey, activeKeys]);

  return (
    <div className="relative rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] p-4 shadow-xs transition hover:border-[rgb(var(--crm-accent)/0.3)]">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-xs font-bold uppercase tracking-wider text-[rgb(var(--crm-text))]">
          {title}
        </h4>
        <span className="text-[11px] text-[rgb(var(--crm-text-muted))]">24h timeline</span>
      </div>
      {loading ? (
        <div className="flex h-[230px] items-center justify-center">
          <div className="flex items-center gap-2 text-xs text-[rgb(var(--crm-text-muted))]">
            <svg className="h-4 w-4 animate-spin text-[rgb(var(--crm-accent))]" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span>Loading chart data…</span>
          </div>
        </div>
      ) : (
        <div className="relative">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${width} ${height}`}
            className="w-full cursor-crosshair select-none overflow-visible"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoverIndex(null)}
            onTouchStart={handleMouseMove}
            onTouchMove={handleMouseMove}
            onTouchEnd={() => setHoverIndex(null)}
          >
            <defs>
              {activeKeys.map((key) => (
                <linearGradient
                  key={key}
                  id={`${gradPrefix}-${key}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={SERIES_CONFIG[key].color} stopOpacity="0.18" />
                  <stop offset="100%" stopColor={SERIES_CONFIG[key].color} stopOpacity="0.0" />
                </linearGradient>
              ))}
            </defs>

            {gridLines.map((ratio, index) => {
              const y = top + plotHeight - ratio * plotHeight;
              return (
                <g key={index}>
                  <line
                    x1={left}
                    x2={width - right}
                    y1={y}
                    y2={y}
                    stroke="rgb(var(--crm-border))"
                    strokeWidth="1"
                    strokeDasharray={ratio === 0 ? undefined : '3 4'}
                    strokeOpacity={ratio === 0 ? 0.8 : 0.6}
                  />
                  <text
                    x={left - 8}
                    y={y + 3}
                    textAnchor="end"
                    fill="rgb(var(--crm-text-muted))"
                    fontSize="9.5"
                    className="font-mono tabular-nums"
                  >
                    {formatValue(ratio * maxVal)}
                  </text>
                </g>
              );
            })}

            {[0, 3, 6, 9, 12, 15, 18, 21].map((h) => {
              const x = left + h * step;
              return (
                <text
                  key={h}
                  x={x}
                  y={height - 8}
                  textAnchor="middle"
                  fill="rgb(var(--crm-text-muted))"
                  fontSize="9.5"
                  className="font-medium"
                >
                  {formatHourLabel(h)}
                </text>
              );
            })}

            {activeKeys.map((key) => {
              const data = seriesData[key] || [];
              const coords = data.map((point) => {
                const x = left + point.hour * step;
                const y = top + plotHeight - ((point[valueKey] || 0) / maxVal) * plotHeight;
                return `${x},${y}`;
              });

              if (coords.length < 2) return null;

              const firstPoint = data[0];
              const lastPoint = data[data.length - 1];
              const firstX = left + (firstPoint ? firstPoint.hour : 0) * step;
              const lastX = left + (lastPoint ? lastPoint.hour : 23) * step;
              const baseY = top + plotHeight;
              const areaPoints = `${firstX},${baseY} ${coords.join(' ')} ${lastX},${baseY}`;

              return (
                <g key={key}>
                  <polygon
                    points={areaPoints}
                    fill={`url(#${gradPrefix}-${key})`}
                  />
                  <polyline
                    fill="none"
                    stroke={SERIES_CONFIG[key].color}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points={coords.join(' ')}
                  />
                </g>
              );
            })}

            {hoverIndex !== null && (
              <line
                x1={left + hoverIndex * step}
                x2={left + hoverIndex * step}
                y1={top}
                y2={top + plotHeight}
                stroke="rgb(var(--crm-text-muted))"
                strokeWidth="1.5"
                strokeDasharray="2 3"
              />
            )}

            {hoverIndex !== null &&
              activePointsAtHover.map(({ key, color, value }) => {
                const x = left + hoverIndex * step;
                const y = top + plotHeight - (value / maxVal) * plotHeight;
                return (
                  <g key={key}>
                    <circle
                      cx={x}
                      cy={y}
                      r="6"
                      fill={color}
                      fillOpacity="0.25"
                    />
                    <circle
                      cx={x}
                      cy={y}
                      r="3.5"
                      fill={color}
                      stroke="rgb(var(--crm-surface))"
                      strokeWidth="2"
                    />
                  </g>
                );
              })}
          </svg>

          {hoverIndex !== null && activePointsAtHover.length > 0 && (
            <div
              className="absolute top-2 z-10 w-52 rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))]/95 p-3 shadow-lg backdrop-blur-md"
              style={{
                left: `${((left + hoverIndex * step) / width) * 100}%`,
                transform: hoverIndex > 14 ? 'translateX(-105%)' : 'translateX(5%)',
              }}
            >
              <div className="mb-2 flex items-center justify-between border-b border-[rgb(var(--crm-border))] pb-1.5 text-[10px] font-bold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
                <span>{formatHourLabel(hoverIndex)}</span>
                <span>Values</span>
              </div>
              <div className="grid gap-1.5">
                {activePointsAtHover.map((pt) => (
                  <div key={pt.key} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex items-center gap-1.5 text-[rgb(var(--crm-text-muted))]">
                      <span className={`h-2 w-2 rounded-full ${SERIES_CONFIG[pt.key].swatch}`} />
                      <span className="truncate">{pt.label}</span>
                    </span>
                    <span className="font-bold text-[rgb(var(--crm-text))] tabular-nums">
                      {formatValue(pt.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SalesOverviewPanel({ token }) {
  const [dateSelect, setDateSelect] = useState('today');
  const [marketplaceSelect, setMarketplaceSelect] = useState('ALL');
  const [fulfillmentSelect, setFulfillmentSelect] = useState('ALL');

  const [filters, setFilters] = useState({
    date: 'today',
    marketplace: 'ALL',
    fulfillmentChannel: 'ALL',
  });

  const [status, setStatus] = useState({ type: 'ready', message: '' });
  const [viewMode, setViewMode] = useState('graph');
  const [syncState, setSyncState] = useState({ status: 'idle', message: '' });

  const [activeSeries, setActiveSeries] = useState({
    today: true,
    yesterday: true,
    lastMonth: true,
    lastYear: true,
  });

  const istTodayLabel = formatIstDateString(new Date());
  const istYesterdayLabel = formatIstDateString(getIstYesterdayDate());

  const cacheKey = useMemo(
    () =>
      `sales-overview|${filters.date}|${filters.marketplace}|${filters.fulfillmentChannel}`,
    [filters.date, filters.marketplace, filters.fulfillmentChannel]
  );

  const fetcher = useCallback(
    () => fetchSalesOverview(token, filters),
    [token, filters]
  );

  const {
    data,
    loading,
    refreshing,
    reload,
    error: loadError,
  } = useCachedResource(token ? cacheKey : null, fetcher, { staleTime: 60_000 });

  useEffect(() => {
    if (loading && !data) {
      setStatus({ type: 'loading', message: '' });
      return;
    }
    if (refreshing && data) {
      setStatus({ type: 'refreshing', message: '' });
      return;
    }
    if (loadError && !data) {
      setStatus({
        type: 'error',
        message: loadError.message || 'Unable to fetch sales snapshot.',
      });
      return;
    }
    if (data) {
      setStatus({ type: 'ready', message: '' });
    }
  }, [loading, refreshing, loadError, data]);

  const loadData = useCallback(
    async ({ force = false } = {}) => {
      if (!token) return;
      try {
        await reload({ force, silent: Boolean(data) && !force });
      } catch {
        // surfaced via status effect
      }
    },
    [token, reload, data]
  );

  const handleApply = () => {
    setFilters({
      date: dateSelect,
      marketplace: marketplaceSelect,
      fulfillmentChannel: fulfillmentSelect,
    });
  };

  /** Reload Sales Order Metrics from Amazon (primary refresh). */
  const handleRefreshMetrics = async () => {
    if (!token || status.type === 'loading' || status.type === 'refreshing') return;
    setSyncState({ status: 'idle', message: '' });
    await loadData({ force: true });
  };

  /** Optional: sync local orders, then reload metrics (helps DB fallback path). */
  const handleSyncOrders = async () => {
    if (!token) return;
    setSyncState({ status: 'syncing', message: 'Syncing orders from Amazon…' });
    try {
      const res = await syncOrders(token, { unshippedOnly: false });
      const jobId = res.jobId || res.job?.id;
      if (!jobId) {
        throw new Error('Could not trigger sync job. Check credentials.');
      }

      const finalJob = await waitForSyncJob(token, jobId, {
        onUpdate: (job) => {
          if (job?.message) {
            setSyncState({ status: 'syncing', message: job.message });
          }
        },
      });

      if (finalJob.status === 'succeeded') {
        setSyncState({
          status: 'succeeded',
          message: 'Orders synced. Reloading sales metrics…',
        });
        invalidateCache('sales-overview|');
        invalidateCache('orders|');
        notifyShippingAlertsRefresh();
        await loadData({ force: true });
        setSyncState({
          status: 'succeeded',
          message: 'Orders synced and sales metrics updated.',
        });
      } else {
        throw new Error(finalJob.error || 'Amazon SP-API order sync failed.');
      }
    } catch (error) {
      setSyncState({
        status: 'failed',
        message: error.message || 'Order sync failed.',
      });
    }
  };

  const currency = data?.summary?.today?.currency || 'INR';

  const activeMetrics = data?.summary?.today || {
    totalOrderItems: 0,
    unitsOrdered: 0,
    orderedProductSales: 0,
    avgUnitsPerOrderItem: 0,
    avgSalesPerOrderItem: 0,
    currency,
  };

  const tableRows = useMemo(() => {
    if (!data?.series) return [];
    return Array.from({ length: 24 }, (_, hour) => {
      const getVal = (period, key) => {
        const point = data.series[period]?.find((p) => p.hour === hour);
        return point ? point[key] : 0;
      };
      return {
        hour,
        todaySales: getVal('today', 'sales'),
        todayUnits: getVal('today', 'units'),
        yesterdaySales: getVal('yesterday', 'sales'),
        yesterdayUnits: getVal('yesterday', 'units'),
        lastMonthSales: getVal('lastMonth', 'sales'),
        lastMonthUnits: getVal('lastMonth', 'units'),
        lastYearSales: getVal('lastYear', 'sales'),
        lastYearUnits: getVal('lastYear', 'units'),
      };
    });
  }, [data]);

  const handleDownloadCSV = () => {
    if (!data?.series) return;

    const headers = [
      'Hour',
      'Today Units',
      `Today Sales (${currency})`,
      'Yesterday Units',
      `Yesterday Sales (${currency})`,
      'Last Month Units',
      `Last Month Sales (${currency})`,
      'Last Year Units',
      `Last Year Sales (${currency})`,
    ];

    const rows = tableRows.map((r) => [
      formatHourLabel(r.hour),
      r.todayUnits,
      r.todaySales,
      r.yesterdayUnits,
      r.yesterdaySales,
      r.lastMonthUnits,
      r.lastMonthSales,
      r.lastYearUnits,
      r.lastYearSales,
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `sales_snapshot_${filters.date}_${filters.marketplace}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <article className={`${panelClassName} overflow-hidden shadow-xs`}>
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] px-5 py-4 sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[rgb(var(--crm-accent))]" />
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[rgb(var(--crm-accent))]">
                Amazon Sales
              </p>
            </div>
            <h2 className="mt-0.5 text-xl font-bold tracking-tight text-[rgb(var(--crm-text))] sm:text-2xl">
              Sales Dashboard
            </h2>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleRefreshMetrics}
            disabled={loading || refreshing || syncState.status === 'syncing'}
            className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--crm-text))] shadow-xs transition hover:bg-[rgb(var(--crm-accent-soft)/0.4)] hover:border-[rgb(var(--crm-accent)/0.35)] disabled:opacity-50"
          >
            <svg
              className={`h-3.5 w-3.5 text-[rgb(var(--crm-accent))] ${refreshing ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            <span>{refreshing ? 'Refreshing…' : 'Refresh'}</span>
          </button>

          <button
            type="button"
            onClick={handleSyncOrders}
            disabled={syncState.status === 'syncing' || loading}
            title="Sync local orders from Amazon, then reload metrics. Sales totals normally come from Order Metrics and do not require this."
            className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--crm-text-muted))] shadow-xs transition hover:bg-[rgb(var(--crm-page-bg))] hover:text-[rgb(var(--crm-text))] disabled:opacity-50"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" strokeLinecap="round" />
              <path d="M12 12v9m0 0l-3-3m3 3l3-3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{syncState.status === 'syncing' ? 'Syncing…' : 'Sync orders'}</span>
          </button>

          <button
            type="button"
            onClick={handleDownloadCSV}
            disabled={!data}
            className="flex items-center gap-1.5 rounded-lg bg-[rgb(var(--crm-accent))] px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs transition hover:bg-[rgb(var(--crm-accent-hover))] disabled:opacity-50"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Download</span>
          </button>
        </div>
      </div>

      {/* Sync Status Banner */}
      {syncState.status !== 'idle' && (
        <div
          className={`border-b px-5 py-2.5 text-xs ${
            syncState.status === 'syncing'
              ? 'border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-accent-soft))] text-[rgb(var(--crm-accent-text))]'
              : syncState.status === 'succeeded'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{syncState.message}</span>
            {syncState.status !== 'syncing' && (
              <button
                type="button"
                className="shrink-0 font-semibold underline"
                onClick={() => setSyncState({ status: 'idle', message: '' })}
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filter Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))]/50 px-5 py-2.5 sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
              Date:
            </span>
            <select
              value={dateSelect}
              onChange={(e) => setDateSelect(e.target.value)}
              className={selectClassName}
            >
              <option value="today">Today — {istTodayLabel}</option>
              <option value="yesterday">Yesterday — {istYesterdayLabel}</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
              Marketplace:
            </span>
            <select
              value={marketplaceSelect}
              onChange={(e) => setMarketplaceSelect(e.target.value)}
              className={selectClassName}
            >
              <option value="ALL">Marketplace total</option>
              <option value="IN">India marketplace</option>
              <option value="US">US marketplace</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
              Fulfillment:
            </span>
            <select
              value={fulfillmentSelect}
              onChange={(e) => setFulfillmentSelect(e.target.value)}
              className={selectClassName}
            >
              <option value="ALL">Both (Amazon and seller)</option>
              <option value="AFN">Amazon fulfilled (FBA)</option>
              <option value="MFN">Seller fulfilled (FBM)</option>
            </select>
          </div>

          <button
            type="button"
            onClick={handleApply}
            disabled={loading}
            className="rounded-lg bg-[rgb(var(--crm-accent))] px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs transition hover:bg-[rgb(var(--crm-accent-hover))] disabled:opacity-50"
          >
            Apply
          </button>
        </div>

        <div className="flex items-center gap-1.5 text-[11px] text-[rgb(var(--crm-text-muted))]">
          <svg className="h-3.5 w-3.5 text-[rgb(var(--crm-accent))]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>Updated: {formatTimestamp(data?.updatedAt)}</span>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        {status.type === 'error' && (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {status.message}
          </p>
        )}

        {/* 5 KPI Metric Cards */}
        <div className="mb-7">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[rgb(var(--crm-accent))]" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-[rgb(var(--crm-text))]">
                Sales Snapshot
              </h3>
            </div>
            <span className="text-[11px] font-medium text-[rgb(var(--crm-text-muted))]">
              {filters.date === 'today' ? 'Today so far' : 'Selected day'} · Amazon Order Metrics
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-2 lg:grid-cols-5">
            {/* 1. Ordered Product Sales (Featured) */}
            <div className="col-span-2 rounded-xl border border-[rgb(var(--crm-accent)/0.35)] bg-[linear-gradient(135deg,rgb(var(--crm-accent-soft)/0.4),rgb(var(--crm-surface))_75%)] p-4 shadow-xs transition hover:border-[rgb(var(--crm-accent)/0.6)] sm:col-span-2 lg:col-span-1">
              <div className="flex items-center justify-between gap-1.5">
                <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[rgb(var(--crm-accent-text))]">
                  <span className="h-2 w-2 rounded-full bg-[rgb(var(--crm-accent))]" />
                  Product Sales
                </span>
                <span className="rounded-full bg-[rgb(var(--crm-accent)/0.12)] px-2 py-0.5 text-[10px] font-bold text-[rgb(var(--crm-accent))]">
                  {currency}
                </span>
              </div>
              <strong className="mt-2.5 block text-2xl font-extrabold tracking-tight text-[rgb(var(--crm-text))] sm:text-3xl tabular-nums">
                {loading ? '—' : formatMoney(activeMetrics.orderedProductSales, currency)}
              </strong>
              <p className="mt-1.5 truncate text-[11px] text-[rgb(var(--crm-text-muted))]">
                {filters.date === 'today' ? 'Today so far' : 'Selected day'} · Order Metrics
              </p>
            </div>

            {/* 2. Total Order Items */}
            <div className="rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] p-4 shadow-xs transition hover:border-[rgb(var(--crm-accent)/0.3)]">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Total Order Items
              </div>
              <strong className="mt-2.5 block text-2xl font-bold tracking-tight text-[rgb(var(--crm-text))] tabular-nums">
                {loading ? '—' : Number(activeMetrics.totalOrderItems || 0).toLocaleString()}
              </strong>
              <p className="mt-1.5 text-[11px] text-[rgb(var(--crm-text-muted))]">
                Lines ordered
              </p>
            </div>

            {/* 3. Units Ordered */}
            <div className="rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] p-4 shadow-xs transition hover:border-[rgb(var(--crm-accent)/0.3)]">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
                <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                Units Ordered
              </div>
              <strong className="mt-2.5 block text-2xl font-bold tracking-tight text-[rgb(var(--crm-text))] tabular-nums">
                {loading ? '—' : Number(activeMetrics.unitsOrdered || 0).toLocaleString()}
              </strong>
              <p className="mt-1.5 text-[11px] text-[rgb(var(--crm-text-muted))]">
                Total item count
              </p>
            </div>

            {/* 4. Avg Units / Order Item */}
            <div className="rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] p-4 shadow-xs transition hover:border-[rgb(var(--crm-accent)/0.3)]">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                Avg. Units / Item
              </div>
              <strong className="mt-2.5 block text-2xl font-bold tracking-tight text-[rgb(var(--crm-text))] tabular-nums">
                {loading ? '—' : Number(activeMetrics.avgUnitsPerOrderItem || 0).toLocaleString()}
              </strong>
              <p className="mt-1.5 text-[11px] text-[rgb(var(--crm-text-muted))]">
                Per ordered item
              </p>
            </div>

            {/* 5. Avg Sales / Order Item */}
            <div className="rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] p-4 shadow-xs transition hover:border-[rgb(var(--crm-accent)/0.3)]">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
                <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
                Avg. Sales / Item
              </div>
              <strong className="mt-2.5 block text-2xl font-bold tracking-tight text-[rgb(var(--crm-text))] tabular-nums">
                {loading ? '—' : formatMoney(activeMetrics.avgSalesPerOrderItem, currency)}
              </strong>
              <p className="mt-1.5 text-[11px] text-[rgb(var(--crm-text-muted))]">
                Average line value
              </p>
            </div>
          </div>
        </div>

        {/* Compare Sales Section */}
        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-[rgb(var(--crm-border))] pb-3">
            <div>
              <h3 className="text-sm font-bold text-[rgb(var(--crm-text))]">Compare Sales Performance</h3>
              <p className="text-[11px] text-[rgb(var(--crm-text-muted))]">
                Hourly trends across key operational comparison periods
              </p>
            </div>

            <div className="inline-flex rounded-lg border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] p-0.5 shadow-2xs">
              <button
                type="button"
                onClick={() => setViewMode('graph')}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  viewMode === 'graph'
                    ? 'bg-[rgb(var(--crm-surface))] text-[rgb(var(--crm-text))] shadow-xs'
                    : 'text-[rgb(var(--crm-text-muted))] hover:text-[rgb(var(--crm-text))]'
                }`}
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" strokeLinecap="round" />
                  <path d="M19 9l-5 5-4-4-3 3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Graph view</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  viewMode === 'table'
                    ? 'bg-[rgb(var(--crm-surface))] text-[rgb(var(--crm-text))] shadow-xs'
                    : 'text-[rgb(var(--crm-text-muted))] hover:text-[rgb(var(--crm-text))]'
                }`}
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h18M3 15h18M9 3v18" />
                </svg>
                <span>Table view</span>
              </button>
            </div>
          </div>

          {viewMode === 'graph' && (
            <div className="grid gap-4 lg:grid-cols-2">
              <ComparisonChart
                title="Units ordered"
                seriesData={data?.series || {}}
                valueKey="units"
                formatValue={(val) => Math.round(val).toLocaleString()}
                activeSeries={activeSeries}
                loading={loading}
              />
              <ComparisonChart
                title="Ordered product sales"
                seriesData={data?.series || {}}
                valueKey="sales"
                formatValue={(val) => formatMoney(val, currency)}
                activeSeries={activeSeries}
                loading={loading}
              />
            </div>
          )}

          {viewMode === 'table' && (
            <div className="max-h-[420px] overflow-x-auto rounded-xl border border-[rgb(var(--crm-border))] shadow-2xs">
              <table className="w-full border-collapse text-left text-xs text-[rgb(var(--crm-text-muted))]">
                <thead className="sticky top-0 z-10 border-b border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-table-head))] font-semibold text-[rgb(var(--crm-text))]">
                  <tr>
                    <th className="p-2.5">Hour</th>
                    {activeSeries.today && <th className="p-2.5 text-right">Today Units</th>}
                    {activeSeries.today && <th className="p-2.5 text-right">Today Sales</th>}
                    {activeSeries.yesterday && <th className="p-2.5 text-right">Yesterday Units</th>}
                    {activeSeries.yesterday && <th className="p-2.5 text-right">Yesterday Sales</th>}
                    {activeSeries.lastMonth && <th className="p-2.5 text-right">Last Month Units</th>}
                    {activeSeries.lastMonth && <th className="p-2.5 text-right">Last Month Sales</th>}
                    {activeSeries.lastYear && <th className="p-2.5 text-right">Last Year Units</th>}
                    {activeSeries.lastYear && <th className="p-2.5 text-right">Last Year Sales</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgb(var(--crm-border))] font-mono tabular-nums">
                  {tableRows.map((row) => (
                    <tr key={row.hour} className="hover:bg-[rgb(var(--crm-accent-soft)/0.3)] transition-colors">
                      <td className="p-2.5 font-medium font-sans text-[rgb(var(--crm-text))]">
                        {formatHourLabel(row.hour)}
                      </td>
                      {activeSeries.today && (
                        <td className="p-2.5 text-right">{row.todayUnits}</td>
                      )}
                      {activeSeries.today && (
                        <td className="p-2.5 text-right font-semibold text-[rgb(var(--crm-text))]">
                          {formatMoney(row.todaySales, currency)}
                        </td>
                      )}
                      {activeSeries.yesterday && (
                        <td className="p-2.5 text-right">{row.yesterdayUnits}</td>
                      )}
                      {activeSeries.yesterday && (
                        <td className="p-2.5 text-right">
                          {formatMoney(row.yesterdaySales, currency)}
                        </td>
                      )}
                      {activeSeries.lastMonth && (
                        <td className="p-2.5 text-right">{row.lastMonthUnits}</td>
                      )}
                      {activeSeries.lastMonth && (
                        <td className="p-2.5 text-right">
                          {formatMoney(row.lastMonthSales, currency)}
                        </td>
                      )}
                      {activeSeries.lastYear && (
                        <td className="p-2.5 text-right">{row.lastYearUnits}</td>
                      )}
                      {activeSeries.lastYear && (
                        <td className="p-2.5 text-right">
                          {formatMoney(row.lastYearSales, currency)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Active Comparison Periods Legend & Toggles */}
          <div className="mt-6 border-t border-[rgb(var(--crm-border))] pt-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
                Comparison Periods
              </span>
              <span className="text-[11px] text-[rgb(var(--crm-text-muted))]">
                Select periods to plot on charts & table
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {Object.entries(SERIES_CONFIG).map(([key, cfg]) => {
                const summaryPeriod = data?.summary?.[key] || {
                  unitsOrdered: 0,
                  orderedProductSales: 0,
                };
                const labelPrefix = key === 'today' ? 'So far' : 'Full day';
                const isChecked = activeSeries[key];
                return (
                  <label
                    key={key}
                    className={`flex cursor-pointer select-none items-start gap-2.5 rounded-xl border p-3 transition-all duration-150 ${
                      isChecked
                        ? 'border-[rgb(var(--crm-accent)/0.3)] bg-[rgb(var(--crm-surface))] shadow-xs hover:border-[rgb(var(--crm-accent)/0.5)]'
                        : 'border-[rgb(var(--crm-border)/0.6)] bg-[rgb(var(--crm-page-bg))]/40 opacity-60 hover:opacity-90'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) =>
                        setActiveSeries((prev) => ({ ...prev, [key]: e.target.checked }))
                      }
                      className="mt-0.5 h-3.5 w-3.5 rounded border-[rgb(var(--crm-border))] text-[rgb(var(--crm-accent))] focus:ring-[rgb(var(--crm-accent))]"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-[rgb(var(--crm-text))]">
                        <span className={`h-2 w-2 rounded-full ${cfg.swatch}`} />
                        <span className="truncate">{cfg.label}</span>
                      </div>
                      <span className="mt-1 block text-[11px] leading-normal text-[rgb(var(--crm-text-muted))]">
                        {labelPrefix}
                      </span>
                      <div className="mt-1 flex items-baseline justify-between gap-1">
                        <span className="text-[11px] font-semibold text-[rgb(var(--crm-text-muted))] tabular-nums">
                          {Number(summaryPeriod.unitsOrdered || 0).toLocaleString()} units
                        </span>
                        <span className="text-[11px] font-bold text-[rgb(var(--crm-text))] tabular-nums">
                          {formatMoney(summaryPeriod.orderedProductSales, currency)}
                        </span>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

